import React, { useState } from 'react';
import SubmitButton from '../../../components/Common/SubmitButton';
import { DistrictModel } from './services/District.service';

interface DistrictFormProps {
  setShowForm: (value: boolean) => void;
  districtDetails: Partial<DistrictModel> | null;
  onSave: (data: Partial<DistrictModel>) => Promise<void>;
}

const DistrictForm: React.FC<DistrictFormProps> = ({ setShowForm, districtDetails, onSave }) => {
  const isEdit = !!districtDetails?.id;
  const [loader, setLoader] = useState(false);

  const [form, setForm] = useState<Partial<DistrictModel>>({
    id: districtDetails?.id || undefined,
    name: districtDetails?.name || '',
    externalId: districtDetails?.externalId || '',
    country: districtDetails?.country || 'US',
    state: districtDetails?.state || '',
    stateName: districtDetails?.stateName || '',
    city: districtDetails?.city || '',
    zip: districtDetails?.zip || '',
    address: districtDetails?.address || '',
    phone: districtDetails?.phone || '',
    website: districtDetails?.website || '',
    districtType: districtDetails?.districtType || '',
    isCharter: districtDetails?.isCharter || false,
  });

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    if (type === 'checkbox') {
      setForm((prev) => ({ ...prev, [name]: (e.target as HTMLInputElement).checked }));
    } else {
      setForm((prev) => ({ ...prev, [name]: value }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoader(true);
    try {
      await onSave(form);
    } finally {
      setLoader(false);
    }
  };

  const isValid = form.name && form.state && form.country;

  return (
    <div className="bg-white rounded-lg p-6 max-w-2xl">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-xl font-bold text-gray-800">
          {isEdit ? 'Edit' : 'Create'} District
        </h2>
        <button
          onClick={() => setShowForm(false)}
          className="text-gray-500 hover:text-gray-700 text-2xl leading-none"
        >
          &times;
        </button>
      </div>

      <form onSubmit={handleSubmit} className="flex flex-col gap-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Name */}
          <div className="md:col-span-2">
            <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
              District Name *
            </label>
            <input
              type="text"
              id="name"
              name="name"
              value={form.name}
              onChange={handleChange}
              placeholder="e.g. Los Angeles Unified"
              className="w-full border border-gray-300 rounded-md p-2 focus:outline-none focus:ring-2 focus:ring-blue-400"
              required
            />
          </div>

          {/* State */}
          <div>
            <label htmlFor="state" className="block text-sm font-medium text-gray-700 mb-1">
              State (abbreviation) *
            </label>
            <input
              type="text"
              id="state"
              name="state"
              value={form.state}
              onChange={(e) =>
                setForm((prev) => ({ ...prev, state: e.target.value.toUpperCase() }))
              }
              placeholder="e.g. CA"
              maxLength={2}
              className="w-full border border-gray-300 rounded-md p-2 focus:outline-none focus:ring-2 focus:ring-blue-400"
              required
            />
          </div>

          {/* Country */}
          <div>
            <label htmlFor="country" className="block text-sm font-medium text-gray-700 mb-1">
              Country *
            </label>
            <input
              type="text"
              id="country"
              name="country"
              value={form.country}
              onChange={handleChange}
              placeholder="US"
              className="w-full border border-gray-300 rounded-md p-2 focus:outline-none focus:ring-2 focus:ring-blue-400"
              required
            />
          </div>

          {/* City */}
          <div>
            <label htmlFor="city" className="block text-sm font-medium text-gray-700 mb-1">
              City
            </label>
            <input
              type="text"
              id="city"
              name="city"
              value={form.city}
              onChange={handleChange}
              placeholder="e.g. Los Angeles"
              className="w-full border border-gray-300 rounded-md p-2 focus:outline-none focus:ring-2 focus:ring-blue-400"
            />
          </div>

          {/* Zip */}
          <div>
            <label htmlFor="zip" className="block text-sm font-medium text-gray-700 mb-1">
              Zip Code
            </label>
            <input
              type="text"
              id="zip"
              name="zip"
              value={form.zip}
              onChange={handleChange}
              placeholder="e.g. 90012"
              className="w-full border border-gray-300 rounded-md p-2 focus:outline-none focus:ring-2 focus:ring-blue-400"
            />
          </div>

          {/* Address */}
          <div className="md:col-span-2">
            <label htmlFor="address" className="block text-sm font-medium text-gray-700 mb-1">
              Address
            </label>
            <input
              type="text"
              id="address"
              name="address"
              value={form.address}
              onChange={handleChange}
              placeholder="e.g. 333 S Beaudry Ave"
              className="w-full border border-gray-300 rounded-md p-2 focus:outline-none focus:ring-2 focus:ring-blue-400"
            />
          </div>

          {/* Phone */}
          <div>
            <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-1">
              Phone
            </label>
            <input
              type="text"
              id="phone"
              name="phone"
              value={form.phone}
              onChange={handleChange}
              placeholder="e.g. (213) 241-1000"
              className="w-full border border-gray-300 rounded-md p-2 focus:outline-none focus:ring-2 focus:ring-blue-400"
            />
          </div>

          {/* Website */}
          <div>
            <label htmlFor="website" className="block text-sm font-medium text-gray-700 mb-1">
              Website
            </label>
            <input
              type="text"
              id="website"
              name="website"
              value={form.website}
              onChange={handleChange}
              placeholder="e.g. https://www.lausd.net"
              className="w-full border border-gray-300 rounded-md p-2 focus:outline-none focus:ring-2 focus:ring-blue-400"
            />
          </div>

          {/* External ID */}
          <div>
            <label htmlFor="externalId" className="block text-sm font-medium text-gray-700 mb-1">
              LEAID (NCES ID)
            </label>
            <input
              type="text"
              id="externalId"
              name="externalId"
              value={form.externalId}
              onChange={handleChange}
              placeholder="e.g. 0622710"
              className="w-full border border-gray-300 rounded-md p-2 focus:outline-none focus:ring-2 focus:ring-blue-400"
            />
          </div>

          {/* District Type */}
          <div>
            <label htmlFor="districtType" className="block text-sm font-medium text-gray-700 mb-1">
              District Type
            </label>
            <input
              type="text"
              id="districtType"
              name="districtType"
              value={form.districtType}
              onChange={handleChange}
              placeholder="e.g. Regular local school district"
              className="w-full border border-gray-300 rounded-md p-2 focus:outline-none focus:ring-2 focus:ring-blue-400"
            />
          </div>

          {/* Is Charter */}
          <div className="flex items-center gap-2 mt-4">
            <input
              type="checkbox"
              id="isCharter"
              name="isCharter"
              checked={form.isCharter || false}
              onChange={handleChange}
              className="h-4 w-4"
            />
            <label htmlFor="isCharter" className="text-sm font-medium text-gray-700">
              Charter District
            </label>
          </div>
        </div>

        <div className="flex justify-end gap-3 mt-2">
          <button
            type="button"
            onClick={() => setShowForm(false)}
            className="px-4 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300"
          >
            Cancel
          </button>
          <SubmitButton title={isEdit ? 'Update' : 'Create'} loader={loader} disable={!isValid} />
        </div>
      </form>
    </div>
  );
};

export default DistrictForm;
